import React, { useState, useCallback, useMemo } from "react";
import SalesDocDetails from "../../_components/Sales/SalesDocDetails";
import { FiFileText, FiHash } from "react-icons/fi";
import { InfoRow, SimpleSelect, FieldInput } from "../../_components/Sales/SalesShared";
import {
    getInvoiceById,
    updateInvoice,
    createInvoice,
    getSalesOrders,
} from "../../_services/sales.service";
import { getCustomers } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";

let customerCache = {};
async function getCachedCustomer(customerName) {
    if (customerCache[customerName]) return customerCache[customerName];
    const res = await getCustomers({ pageSize: 1000, page: 1, search: customerName, searchType: "name" });
    customerCache[customerName] = res?.data || [];
    return customerCache[customerName];
}

async function fetchCustomerNames() {
    const res = await getCustomers({ pageSize: 1000, page: 1 });
    const names = (res?.data || [])
        .filter(r => (r.status || r.Status || "").toLowerCase() === "active")
        .map(r => r.name)
        .filter(Boolean);
    return [...new Set(names)].sort();
}

async function fetchContactPersons(customerName) {
    if (!customerName) return [];
    const data = await getCachedCustomer(customerName);
    return data
        .filter(r => r.name === customerName && !!r.attention && (r.status || r.Status || "").toLowerCase() === "active")
        .map(r => r.attention)
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i);
}

async function fetchCustomerDetails(customerName) {
    if (!customerName) return {};
    const data = await getCachedCustomer(customerName);
    const match = data.find(r => r.name === customerName);
    if (!match) return {};
    return {
        paymentTerms: match.creditTerms || "",
        customerAddress: match.billingAddress || match.address || match.shippingAddress || "",
        shippingAddress: match.shippingAddress || "",
        phoneNumber: match.phoneNumber || match.phone_number || "",
    };
}

export default function InvoiceDetails() {
    const [salesOrders, setSalesOrders] = useState([]);
    const [selectedSO, setSelectedSO] = useState(null);

    const fetchSalesOrders = useCallback((customerName, contactPerson) => {
        if (!customerName || !contactPerson) {
            setSalesOrders([]);
            setSelectedSO(null); // clear stale fill data
            return;
        }
        getSalesOrders({ pageSize: 500, page: 1, search: customerName, searchType: "customer", status: "Approved" })
            .then(r => {
                const all = r?.data || [];
                setSalesOrders(all.filter(d => d.contactPerson === contactPerson));
            })
            .catch(() => setSalesOrders([]));
    }, []);

    const handleSOSelect = useCallback((soId) => {
        const found = salesOrders.find(d => d.so_id === soId || d.id === soId);
        setSelectedSO(found || null);
    }, [salesOrders]);

    const fillFromSO = useMemo(() => {
        if (!selectedSO) return null;
        return {
            customer: selectedSO.customer || "",
            contactPerson: selectedSO.contactPerson || "",
            phoneNumber: selectedSO.phoneNumber || selectedSO.phone_number || "",
            customerAddress: selectedSO.customer_address || selectedSO.customerAddress || "",
            paymentTerms: selectedSO.payment_terms || selectedSO.paymentTerms || "",
            items: (selectedSO.items || []).map(item => ({ ...item, selected: true })),
            soRef: selectedSO.id || selectedSO.so_id,
            custPONo: selectedSO.custPONo || selectedSO.cust_po_no || "",
        };
    }, [selectedSO]);

    const invExtra = useMemo(() => ({
        left: ({ draft, onChange, errors, edit, salesOrders: availableSOs }) => (
            edit ? (
                <>
                    <SimpleSelect
                        label="SO Reference No."
                        name="soRef"
                        value={draft?.soRef || ""}
                        onChange={(e) => {
                            onChange(e);
                            handleSOSelect(e.target.value);
                        }}
                        options={(availableSOs || []).map(d => d.id)}
                        placeholder="Select Sales Order (Approved)..."
                        disabled={!draft?.customer || !draft?.contactPerson || (availableSOs || []).length === 0}
                        required
                        error={errors?.so_ref}
                    />
                    <FieldInput
                        label="Customer PO No."
                        name="custPONo"
                        value={draft?.custPONo || ""}
                        onChange={onChange}
                        readOnly={true}
                    />
                </>
            ) : (
                <>
                    {draft?.soRef || draft?.so_ref || draft?.so_id || draft?.soReference || draft?.quotationRef
                        ? <InfoRow icon={FiFileText} label="SO Reference No.">{draft.soRef || draft.so_ref || draft.so_id || draft.soReference || draft.quotationRef}</InfoRow>
                        : null
                    }
                    {draft?.custPONo || draft?.cust_po_no
                        ? <InfoRow icon={FiHash} label="Cust PO No.">{draft.custPONo || draft.cust_po_no}</InfoRow>
                        : null
                    }
                </>
            )
        ),
        right: null,
    }), [handleSOSelect]);

    const fetchCustDetails = useCallback((name) => fetchCustomerDetails(name), []);
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);

    return (
        <SalesDocDetails
            docType="Invoice"
            basePath="/sales/invoices"
            fetchById={getInvoiceById}
            updateItem={updateInvoice}
            createItem={createInvoice}
            fetchCustomerNames={fetchCustomerNames}
            fetchContactPersons={fetchContactPersons}
            fetchCustomerDetails={fetchCustDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            statuses={["Draft", "Pending for Approval", "Approved", "Rejected"]}
            emptyDraft={{ soRef: "", custPONo: "" }}
            extraFields={invExtra}
            addressLabel="Customer Address"
            addressField="customerAddress"
            lineItemProps={{ selectable: false, readOnly: true }}
            fillDraft={fillFromSO}
            onDependentFieldChange={fetchSalesOrders}
            extraFieldProps={{ salesOrders }}
            hidePaymentTerms={false}
        />
    );
}